"use client";

import { useEffect, useState } from "react";
import { VideoBackground } from "@/components/custom/video-theme";

const stats = [
	{ value: "1.5B+", label: "Views Delivered" },
	{ value: "125M+", label: "Viewers Reached" },
	{ value: "810K+", label: "Hours Promoted" },
	{ value: "2,500+", label: "Creators Trust Us" },
];

const focusIntervalMs = 2600;

export default function Stats() {
	const [focusedIndex, setFocusedIndex] = useState(0);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const visibleFocusedIndex = hoveredIndex ?? focusedIndex;

	useEffect(() => {
		if (hoveredIndex !== null) return;

		const intervalId = window.setInterval(() => {
			setFocusedIndex((currentIndex) => (currentIndex + 1) % stats.length);
		}, focusIntervalMs);

		return () => window.clearInterval(intervalId);
	}, [hoveredIndex]);

	return (
		<VideoBackground variant="light">
			<section className="py-24 bg-transparent text-zinc-900">
				<div className="container mx-auto px-4 md:px-6">
					<div
						className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4 xl:gap-10"
						data-testid="homepage-stats-grid"
					>
						{stats.map((stat, index) => (
							<button
								type="button"
								key={stat.label}
								className="group mx-auto flex w-full max-w-xs cursor-pointer appearance-none flex-col items-center border-0 bg-transparent p-0 text-center animate-on-scroll-view focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50"
								aria-pressed={index === visibleFocusedIndex}
								onMouseEnter={() => {
									setHoveredIndex(index);
									setFocusedIndex(index);
								}}
								onMouseLeave={() => setHoveredIndex(null)}
								onFocus={() => {
									setHoveredIndex(index);
									setFocusedIndex(index);
								}}
								onBlur={() => setHoveredIndex(null)}
								onClick={() => setFocusedIndex(index)}
							>
								<span
									className={`block text-5xl font-black tracking-tighter transition-[color,transform,text-shadow] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] sm:text-6xl lg:text-7xl 2xl:text-8xl ${
										index === visibleFocusedIndex
											? "scale-[1.04] text-[#dc2626] [text-shadow:0_16px_44px_rgba(220,38,38,0.22)]"
											: "scale-100 text-zinc-200 [text-shadow:none] group-hover:text-[#dc2626]"
									}`}
									data-focused={
										index === visibleFocusedIndex ? "true" : "false"
									}
								>
									{stat.value}
								</span>
								<span className="text-sm font-bold uppercase tracking-widest text-zinc-500 mt-2">
									{stat.label}
								</span>
							</button>
						))}
					</div>
					<p className="text-center text-xs text-zinc-400 mt-8 font-inter">
						Cumulative figures across all managed campaigns since inception.
						Individual results vary.
					</p>
				</div>
			</section>
		</VideoBackground>
	);
}
